/**
 * Stop a tab whose identity changed underneath it.
 *
 * The Auth0 token cache is in `localStorage`, shared by every tab of the
 * origin. When another tab signs in as someone else, or switches workspace or
 * organization, THIS tab's next `getAccessToken()` returns the other identity's
 * token — while its pages, its tenancy, its caches and its keyspaces still
 * belong to the old one. Requests go out as the new identity and their answers
 * render under the old one's header: one workspace's data shown as another's,
 * and cached under the old identity's keys.
 *
 * Requests and streams refuse mismatching tokens. This guard also watches
 * storage, focus and a bounded timer, then unmounts the protected app, clears
 * its caches and explains why it must reload. Fresh grants detect role, team
 * and plan changes even when the identity itself stays the same.
 *
 * The notice is not dismissible: the previous app can no longer be trusted to
 * show current access. Reload remains explicit so the transition is explained.
 */
import * as React from "react";
import { sessionAccessKey, tokenMatchesSession, type Session, type SessionManager } from "@authe/fe-commons/session";
import { useSession } from "@authe/ui-core/session";
import { SESSION_IDENTITY_DRIFT_EVENT } from "./routing-token";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@authe/ui-core";

/** How often to re-check without a storage event (a same-tab silent refresh). */
const CHECK_INTERVAL_MS = 30_000;
const GRANT_REFRESH_MS = 60_000;
type SessionDrift = "identity" | "access" | "ended" | null;

export function IdentityDriftGuard({ onDrift, children }: { onDrift?: () => void; children?: React.ReactNode }): React.ReactElement | null {
  const { session, isAuthenticated, manager } = useSession();
  const drifted = useIdentityDrift(isAuthenticated ? session : null, manager, onDrift);
  if (!drifted) return <>{children}</>;
  return (
    <AlertDialog open>
      <AlertDialogContent onEscapeKeyDown={(event) => event.preventDefault()}>
        <AlertDialogHeader>
          <AlertDialogTitle>{drifted === "access" ? "Your access has changed" : drifted === "ended" ? "Your session has ended" : "You switched account or workspace in another tab"}</AlertDialogTitle>
          <AlertDialogDescription>
            {drifted === "access" ? "Your roles, team access or plan changed. Reload to continue with your current permissions."
              : drifted === "ended" ? "Reload to sign in again. This tab has stopped loading workspace data."
                : "This tab still shows the previous account or workspace. Reload to continue in the one you are signed in to."}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction onClick={() => window.location.reload()}>Reload</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/**
 * Why the browser's live identity or access no longer matches `session`.
 *
 * Sticky: once drifted, the tab stays stopped until it reloads — a token that
 * flips back does not make what was loaded in between trustworthy.
 */
export function useIdentityDrift(
  session: Session | null,
  manager: SessionManager,
  onDrift?: () => void,
): SessionDrift {
  const [drifted, setDrifted] = React.useState<SessionDrift>(null);
  const onDriftRef = React.useRef(onDrift);
  React.useEffect(() => { onDriftRef.current = onDrift; });
  // Invalidate only after the protected subtree has unmounted, so it cannot
  // immediately refetch or keep streaming with obsolete permissions.
  React.useEffect(() => {
    if (!drifted) return;
    onDriftRef.current?.();
    void manager.invalidateAccess();
  }, [drifted, manager]);

  React.useEffect(() => {
    if (!session || drifted) return undefined;
    let active = true;
    // A sign-out or switch started in THIS tab is not drift: its own teardown
    // and navigation handle it.
    let leaving = false;
    let checking = false;
    let lastGrantRefresh = Date.now();
    const offIdentity = manager.subscribeIdentityChange(() => { leaving = true; });
    const check = async () => {
      if (checking || leaving || document.visibilityState === "hidden") return;
      checking = true;
      try {
        const token = await manager.getAccessToken();
        if (!active || leaving) return;
        if (token && !tokenMatchesSession(token, session)) {
          setDrifted("identity");
          return;
        }
        if (token && (session.claims.impersonated || Date.now() - lastGrantRefresh < GRANT_REFRESH_MS)) return;
        lastGrantRefresh = Date.now();
        const fresh = await manager.refreshSession();
        if (!active || leaving) return;
        if (!fresh) setDrifted("ended");
        else if (!tokenMatchesSession(fresh.accessToken, session)) setDrifted("identity");
        else if (sessionAccessKey(fresh) !== sessionAccessKey(session)) setDrifted("access");
      } catch (error) {
        // Offline / transient errors are not evidence of revoked membership.
        // Authoritative denials are terminal; APIs also enforce access live.
        if (active && !leaving && (error as { error?: string })?.error === "access_denied") setDrifted("ended");
      } finally {
        checking = false;
      }
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key === null || event.key.startsWith("@@auth0spajs@@")) void check();
    };
    const onRequestDrift = () => { if (active && !leaving) setDrifted("identity"); };
    window.addEventListener(SESSION_IDENTITY_DRIFT_EVENT, onRequestDrift);
    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", check);
    document.addEventListener("visibilitychange", check);
    const timer = window.setInterval(() => { void check(); }, CHECK_INTERVAL_MS);
    return () => {
      active = false;
      offIdentity();
      window.removeEventListener(SESSION_IDENTITY_DRIFT_EVENT, onRequestDrift);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", check);
      document.removeEventListener("visibilitychange", check);
      window.clearInterval(timer);
    };
  }, [session, manager, drifted]);

  return drifted;
}
